import { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import { Popover, PopoverContent, PopoverTrigger } from "@monorepo-apps/ui/src/components/popover";

import { architectureComponents } from "@/presentation/data/architecture-components";
import { DOMAIN_LABEL } from "@/presentation/data/domains";

interface ComponentsMultiselectProps {
  value: string[];
  onChange: (next: string[]) => void;
}

export function ComponentsMultiselect({ value, onChange }: ComponentsMultiselectProps) {
  const [open, setOpen] = useState(false);
  const selected = new Set(value);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const grouped = new Map<string, typeof architectureComponents>();
  for (const c of architectureComponents) {
    const bucket = (grouped.get(c.domain) as typeof architectureComponents | undefined) ?? [];
    grouped.set(c.domain, [...bucket, c]);
  }

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-full justify-between"
            type="button"
          >
            <span className="truncate text-[12px]">
              {value.length === 0
                ? "Applies to all components"
                : `${value.length} component${value.length === 1 ? "" : "s"} selected`}
            </span>
            <ChevronDown className="size-3.5 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="max-h-[400px] w-[420px] overflow-y-auto p-0" align="start">
          <div className="flex flex-col">
            {Array.from(grouped.entries()).map(([domain, list]) => (
              <div key={domain} className="flex flex-col">
                <div className="bg-muted/40 text-muted-foreground sticky top-0 px-3 py-1.5 text-[10px] font-medium tracking-[0.12em] uppercase">
                  {DOMAIN_LABEL[domain as keyof typeof DOMAIN_LABEL]}
                </div>
                {list.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggle(c.id)}
                    className="hover:bg-muted/50 flex items-center justify-between gap-2 px-3 py-1.5 text-left text-[12.5px]"
                  >
                    <span className="line-clamp-1">{c.name}</span>
                    {selected.has(c.id) ? <Check className="text-primary size-3.5" /> : null}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {value.map((id) => {
            const c = architectureComponents.find((x) => x.id === id);
            if (!c) return null;
            return (
              <span
                key={id}
                className="bg-muted/60 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px]"
              >
                {c.name}
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  className="hover:text-destructive transition-colors"
                  aria-label={`Remove ${c.name}`}
                >
                  <X className="size-2.5" />
                </button>
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
